"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StereotypeClient = void 0;
const axios_1 = __importDefault(require("axios"));
const axios_retry_1 = __importDefault(require("axios-retry"));
const js_base64_1 = require("js-base64");
const constants_1 = require("./utils/constants");
const helper_1 = require("./utils/helper");
class StereotypeClient {
    constructor(accessToken, options) {
        this.accessToken = accessToken.substring(accessToken.indexOf(' ') + 1);
        this.baseUrl = (options === null || options === void 0 ? void 0 : options.baseUrl) || constants_1.defaultConfigurations.baseUrl;
        if (this.baseUrl[this.baseUrl.length - 1] === '/') {
            this.baseUrl = this.baseUrl.slice(0, -1);
        }
        this.timeout = (options === null || options === void 0 ? void 0 : options.timeout) || constants_1.defaultConfigurations.timeout;
        this.numRetries = (options === null || options === void 0 ? void 0 : options.numRetries) || constants_1.defaultConfigurations.numRetries;
        this.curies = {};
        this.defaultAxiosConfig = { headers: { Authorization: `Bearer ${this.accessToken}` }, timeout: this.timeout, baseURL: this.baseUrl };
        (0, axios_retry_1.default)(axios_1.default, { retries: this.numRetries });
    }
    setAcceptHeader(headerValue) {
        this.acceptHeader = headerValue;
    }
    setBlacklistHeader(headerValue) {
        this.blacklistHeader = headerValue;
    }
    setWhitelistHeader(headerValue) {
        this.whitelistHeader = headerValue;
    }
    setAcceptPreferenceHeader(headerValue) {
        this.acceptPreferenceHeader = headerValue;
    }
    setCurieHeader(headerValue) {
        this.curieHeader = headerValue;
    }
    setMaximumCrawlDepthHeader(headerValue) {
        this.maximumCrawlDepthHeader = String(headerValue);
    }
    setCrawlerSoftErrors(headerValue) {
        this.crawlerSoftErrors = headerValue;
    }
    setIsBinaryResponse(binary) {
        this.isBinaryResponse = binary;
    }
    setCurie(rel, replacement) {
        this.curies[rel] = replacement;
    }
    _constructCurieHeader() {
        return Object.keys(this.curies)
            .map(k => k + constants_1.CURIE_SEPARATOR + this.curies[k])
            .join(',');
    }
    async listTemplates(skipCache = false, includePublic = false, templateTypes, metadata) {
        const { accessToken, baseUrl } = this;
        const metadataString = metadata ? JSON.stringify(metadata) : null;
        const params = { templateType: templateTypes, metadata: metadataString, skip_cache: (skipCache ? Math.random() : null), public: includePublic };
        return axios_1.default
            .get('/v1/templates', { headers: { Authorization: `Bearer ${accessToken}` }, baseURL: baseUrl, params })
            .then(res => res.data);
    }
    async getTemplateById(templateId, skipCache = false, doNotAddBody = false) {
        const templateUrl = this._getUrl(`/v1/templates/${encodeURIComponent(templateId)}`);
        return this.getTemplate(templateUrl, skipCache, doNotAddBody);
    }
    async getTemplate(templateUrl, skipCache = false, doNotAddBody = false) {
        if (!templateUrl) {
            return Promise.reject({
                status: 404,
                message: 'Template not found! Empty template ID provided.'
            });
        }
        if (doNotAddBody) {
            return this._getTemplateInfo(templateUrl, skipCache);
        }
        const data = await Promise.all([
            this._getTemplateInfo(templateUrl, skipCache),
            this._getTemplateBody(templateUrl, skipCache)
        ]);
        return Object.assign({}, data[0], { templateBody: js_base64_1.Base64.encode(data[1]) });
    }
    async _getTemplateBody(templateUrl, skipCache) {
        const { accessToken } = this;
        const verifiedTemplateUrl = this._verifyTemplateUrl('/v1/templates', templateUrl);
        return axios_1.default
            .get(verifiedTemplateUrl, { headers: { Authorization: `Bearer ${accessToken}`, Accept: '*/*' }, params: { skip_cache: (skipCache ? Math.random() : null) } })
            .then(res => {
            const response = typeof (res.data) === 'object' ? JSON.stringify(res.data) : res.data;
            return response;
        });
    }
    async _getTemplateInfo(templateUrl, skipCache) {
        const { accessToken } = this;
        const verifiedTemplateUrl = this._verifyTemplateUrl('/v1/templates', templateUrl);
        return axios_1.default
            .get(verifiedTemplateUrl, { headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' }, params: { skip_cache: (skipCache ? Math.random() : null) } })
            .then(res => res.data);
    }
    async putTemplateById(putTemplateRequest, isPublic) {
        const templateUrl = this._getUrl(`/v1/templates/${encodeURIComponent(putTemplateRequest.templateId)}`);
        return this.putTemplate(templateUrl, putTemplateRequest, isPublic);
    }
    async putTemplate(templateUrl, putTemplateRequest, isPublic = false) {
        const verifiedTemplateUrl = this._verifyTemplateUrl('/v1/templates', templateUrl);
        return this._createTemplate(verifiedTemplateUrl, 'PUT', putTemplateRequest, isPublic)
            .then(res => res.data);
    }
    _createTemplate(templateUrl, method, postTemplateRequest, isPublic = false) {
        const { templateType, contentType, templateBody, templateName, templateDescription, metadata } = postTemplateRequest;
        const metadataString = Array.isArray(metadata) ? JSON.stringify(metadata) : '';
        if (!['POST', 'PUT'].includes(method)) {
            return Promise.reject(new Error('You should pass POST or PUT for the method parameter'));
        }
        if (!(0, helper_1._isSupportedContentType)(contentType)) {
            return Promise.reject(new Error(`Invalid content type: ${contentType}`));
        }
        const headers = {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': contentType,
            'x-cimpress-template-public': isPublic.toString(),
            'x-cimpress-template-type': templateType ? encodeURIComponent(templateType) : 'raw',
            'x-cimpress-template-name': templateName ? encodeURIComponent(templateName) : '',
            'x-cimpress-template-description': templateDescription ? encodeURIComponent(templateDescription) : '',
            'x-cimpress-template-metadata': encodeURIComponent(metadataString),
            'Accept': 'application/json'
        };
        return (0, axios_1.default)({ url: templateUrl, method, headers, data: (templateBody || '') });
    }
    async createTemplate(postTemplateRequest, isPublic = false) {
        const templatesUrl = this._getUrl('/v1/templates');
        return this._createTemplate(templatesUrl, 'POST', postTemplateRequest, isPublic)
            .then(res => res.data);
    }
    async deleteTemplateById(templateId, skipCache = false) {
        const templateUrl = this._getUrl(`/v1/templates/${templateId}`);
        return this.deleteTemplate(templateUrl, skipCache);
    }
    async deleteTemplate(templateUrl, skipCache = false) {
        const verifiedTemplateUrl = this._verifyTemplateUrl('/v1/templates', templateUrl);
        const config = Object.assign(Object.assign({}, this.defaultAxiosConfig), { params: { skip_cache: (skipCache ? Math.random() : null) } });
        return axios_1.default.delete(verifiedTemplateUrl, config)
            .then(res => res.status);
    }
    async materializeById(templateId, propertyBag, getMaterializationId = false, skipCache = false, contentType = 'application/json') {
        return this.materializeSyncById(templateId, propertyBag, getMaterializationId, skipCache, contentType)
            .then(resultStruct => resultStruct.result);
    }
    async materialize(templateId, propertyBag, getMaterializationId = false, skipCache = false, contentType = 'application/json') {
        return this.materializeSync(templateId, propertyBag, getMaterializationId, skipCache, contentType)
            .then(resultStruct => resultStruct.result);
    }
    async materializeDirect(template, propertyBag, skipCache = false, preferAsync = false, contentType = 'application/json') {
        const headers = {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': contentType,
            'x-cimpress-link-timeout': this.timeout
        };
        if (this.acceptHeader) {
            headers['Accept'] = this.acceptHeader;
        }
        if (this.blacklistHeader) {
            headers['x-cimpress-rel-blacklist'] = this.blacklistHeader;
        }
        if (this.whitelistHeader) {
            headers['x-cimpress-rel-whitelist'] = this.whitelistHeader;
        }
        if (this.acceptPreferenceHeader) {
            headers['x-cimpress-accept-preference'] = this.acceptPreferenceHeader;
        }
        if (this.curieHeader) {
            headers['x-cimpress-rel-curies'] = this.curieHeader;
        }
        else if (Object.keys(this.curies).length) {
            headers['x-cimpress-rel-curies'] = this._constructCurieHeader();
        }
        if (this.maximumCrawlDepthHeader) {
            headers['x-cimpress-max-depth'] = this.maximumCrawlDepthHeader;
        }
        if (this.crawlerSoftErrors) {
            headers['x-cimpress-crawler-soft-errors'] = this.crawlerSoftErrors;
        }
        if (preferAsync) {
            headers.prefer = 'respond-async';
        }
        const config = Object.assign(Object.assign({}, this.defaultAxiosConfig), { headers, params: { skip_cache: (skipCache ? Math.random() : null) } });
        if (this.isBinaryResponse) {
            config.responseType = 'arraybuffer';
        }
        return axios_1.default
            .post('/v1/materializations', {
            template: {
                body: js_base64_1.Base64.encode(template.content),
                contentType: template.contentType
            },
            templatePayload: propertyBag
        }, config)
            .then(res => ({
            status: res.status,
            result: res.data,
            contentType: res.headers['content-type']
        }));
    }
    async materializeSyncById(templateId, propertyBag, getMaterializationId = false, skipCache = false, contentType = 'application/json') {
        const templateUrl = this._getUrl(`/v1/templates/${encodeURIComponent(templateId)}`);
        return this._materialize(templateUrl, propertyBag, getMaterializationId, false, skipCache, contentType);
    }
    async materializeSync(templateUrl, propertyBag, getMaterializationId = false, skipCache = false, contentType = 'application/json') {
        return this._materialize(templateUrl, propertyBag, getMaterializationId, false, skipCache, contentType);
    }
    async materializeAsync(templateUrl, propertyBag, getMaterializationId = false, skipCache = false, contentType = 'application/json') {
        return this._materialize(templateUrl, propertyBag, getMaterializationId, true, skipCache, contentType);
    }
    _getUrl(path) {
        return this.baseUrl + path;
    }
    _verifyTemplateUrl(path, templateUrl) {
        const parts = templateUrl.split('/');
        if (parts.length <= 0) {
            throw new Error('Invalid template URL (parts)');
        }
        if (templateUrl !== (`${this.baseUrl + path}/${parts[parts.length - 1]}`)) {
            throw new Error(`Invalid template URL (format) ${templateUrl} :: ${`${this.baseUrl + path}/${parts[parts.length - 1]}`}`);
        }
        return templateUrl;
    }
    async _materialize(templateUrl, propertyBag, getMaterializationId = false, preferAsync = false, skipCache = false, contentType = 'application/json') {
        const verifiedTemplateUrl = this._verifyTemplateUrl('/v1/templates', templateUrl);
        const parts = verifiedTemplateUrl.split('/');
        const templateId = parts[parts.length - 1];
        const templatesMaterializationUrl = this._getUrl(`/v1/templates/${templateId}/materializations`);
        const headers = {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': contentType,
            'x-cimpress-link-timeout': this.timeout
        };
        if (this.acceptHeader) {
            headers['Accept'] = this.acceptHeader;
        }
        if (this.blacklistHeader) {
            headers['x-cimpress-rel-blacklist'] = this.blacklistHeader;
        }
        if (this.whitelistHeader) {
            headers['x-cimpress-rel-whitelist'] = this.whitelistHeader;
        }
        if (this.acceptPreferenceHeader) {
            headers['x-cimpress-accept-preference'] = this.acceptPreferenceHeader;
        }
        if (this.curieHeader) {
            headers['x-cimpress-rel-curies'] = this.curieHeader;
        }
        else if (Object.keys(this.curies).length) {
            headers['x-cimpress-rel-curies'] = this._constructCurieHeader();
        }
        if (this.maximumCrawlDepthHeader) {
            headers['x-cimpress-max-depth'] = this.maximumCrawlDepthHeader;
        }
        if (this.crawlerSoftErrors) {
            headers['x-cimpress-crawler-soft-errors'] = this.crawlerSoftErrors;
        }
        if (preferAsync) {
            headers.prefer = 'respond-async';
        }
        const config = Object.assign(Object.assign({}, this.defaultAxiosConfig), { headers, params: { skip_cache: (skipCache ? Math.random() : null) } });
        if (this.isBinaryResponse) {
            config.responseType = 'arraybuffer';
        }
        return axios_1.default.post(templatesMaterializationUrl, propertyBag, config)
            .then((res) => {
            if (getMaterializationId && res.headers && res.headers.location) {
                const preStringLen = 'v1'.length + '/materializations/'.length + 1;
                return {
                    status: res.status,
                    result: res.headers.location.substring(preStringLen),
                    contentType: res.headers['content-type']
                };
            }
            else if (res.status === 202) {
                return {
                    status: res.status,
                    result: res.headers.location,
                    contentType: res.headers['content-type']
                };
            }
            else {
                return {
                    status: res.status,
                    result: res.data,
                    contentType: res.headers['content-type']
                };
            }
        });
    }
    async getMaterializationById(idMaterialization, skipCache = false) {
        const { accessToken, timeout, isBinaryResponse } = this;
        const headers = {
            Authorization: `Bearer ${accessToken}`
        };
        const config = { headers, timeout: timeout, baseURL: this.baseUrl, params: { skip_cache: (skipCache ? Math.random() : null) } };
        if (isBinaryResponse) {
            config.responseType = 'arraybuffer';
        }
        return axios_1.default
            .get(`/v1/materializations/${idMaterialization}`, config)
            .then(res => res.data);
    }
    async expand(propertyBag, skipCache = false, contentType = 'application/json') {
        const headers = {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': contentType,
            'x-cimpress-link-timeout': this.timeout
        };
        if (this.blacklistHeader) {
            headers['x-cimpress-rel-blacklist'] = this.blacklistHeader;
        }
        if (this.whitelistHeader) {
            headers['x-cimpress-rel-whitelist'] = this.whitelistHeader;
        }
        if (this.acceptPreferenceHeader) {
            headers['x-cimpress-accept-preference'] = this.acceptPreferenceHeader;
        }
        if (this.curieHeader) {
            headers['x-cimpress-rel-curies'] = this.curieHeader;
        }
        else if (Object.keys(this.curies).length) {
            headers['x-cimpress-rel-curies'] = this._constructCurieHeader();
        }
        if (this.maximumCrawlDepthHeader) {
            headers['x-cimpress-max-depth'] = this.maximumCrawlDepthHeader;
        }
        if (this.crawlerSoftErrors) {
            headers['x-cimpress-crawler-soft-errors'] = this.crawlerSoftErrors;
        }
        const config = Object.assign(Object.assign({}, this.defaultAxiosConfig), { headers, params: { skip_cache: (skipCache ? Math.random() : null) } });
        if (this.isBinaryResponse) {
            config.responseType = 'arraybuffer';
        }
        return axios_1.default
            .post('/v1/expand', propertyBag, config)
            .then(res => res.data);
    }
    async livecheck(skipCache = false) {
        const { defaultAxiosConfig } = this;
        const config = Object.assign(Object.assign({}, defaultAxiosConfig), { params: { skip_cache: (skipCache ? Math.random() : null) } });
        return axios_1.default
            .get('/livecheck', config)
            .then(res => (res && res.status === 200));
    }
    async getSwagger(skipCache = false) {
        const { defaultAxiosConfig } = this;
        const config = Object.assign(Object.assign({}, defaultAxiosConfig), { params: { skip_cache: (skipCache ? Math.random() : null) } });
        return axios_1.default
            .get('/v1/swagger.json', config)
            .then(res => res.data);
    }
}
exports.StereotypeClient = StereotypeClient;
